import Pane from 'components/Common/Pane'
import cn from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import OfferPageRoomGallery from '../OfferPageRoomGallery'
import MarkdownRender from 'components/Luxkit/MarkdownRender'
import config from 'constants/config'
import { rem } from 'polished'
import PaneDetailsSection from 'components/Common/Pane/PaneDetailsSection'
import List from 'components/Common/List'
import LinePlaneFlyIcon from 'components/Luxkit/Icons/line/LinePlaneFlyIcon'
import moment from 'moment'
import FormatCurrency from 'components/Common/FormatCurrency'
import useToggle from 'hooks/useToggle'
import { accountAccessShowLogin } from 'actions/UiActions'

import { useAppDispatch } from 'hooks/reduxHooks'
import { sortBy } from 'lib/arrayUtils'
import useIsAvailableForPurchase from '../hooks/useIsAvailableForPurchase'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPageFilterTypes } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { OfferPackageOptionView } from 'selectors/offerPageSelectors'
import AdditionalGuestsPopup from 'components/Booking/AdditionalGuestsPopup'
import { includedGuestsExceededItems } from 'lib/cartReservationUtils'
import TaxesAndFees from 'components/OfferPage/PackageOption/Pricing/TaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import LastMinuteDiscountText from './LastMinuteDiscountText'
import LineCameraIcon from 'components/Luxkit/Icons/line/LineCameraIcon'
import SmallToggleButton from 'components/Luxkit/Button/SmallToggleButton'
import TextLink from 'components/Luxkit/TextLink'
import WalledContent from 'components/Common/WalledContent'
import TextButton from 'components/Luxkit/Button/TextButton'
import PriceTagIcon from 'components/Luxkit/Icons/custom/PriceTagIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import OfferBookButton from '../OfferBookButton'
import Caption from 'components/Luxkit/Typography/Caption'
import useHotelPriceColour from 'hooks/useHotelPriceColour'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import Heading from 'components/Luxkit/Typography/Heading'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton/BookmarkButton'
import TitleGrid from '../PackageOption/TitleGrid'
import { useInclusionTravelPeriodFilter } from 'hooks/useInclusionTravelPeriodFilter'
import zIndex from 'styles/tools/z-index'
import { ImageParams } from 'components/Common/ResponsiveImage'
import ImageGalleryLightbox from 'components/Luxkit/Carousel/ImageGalleryLightbox'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import BodyTextBlock from 'components/Luxkit/TextBlocks/BodyTextBlock'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'

const PackageDescription = styled.div`
  overflow-y: hidden;
  position: relative;
  flex-grow: 1;
  max-height: ${rem(213)};

  &::after {
    content: "";
    display: block;
    height: ${rem(48)};
    left: 0;
    right: 0;
    position: absolute;
    bottom:0;
    top: ${rem(165)};
    z-index: ${zIndex.min};
    background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 85%);
  }
`

const ContentGrid = styled.div`
  display: grid;
  grid-gap: ${rem(16)};
  grid-template-columns: 1fr;
  padding: ${rem(16)} ${rem(24)};

  ${mediaQueryUp.sm} {
    grid-template-columns: 1fr 1fr;
    grid-gap: ${rem(28)};
    padding: ${rem(24)};
  }

  ${mediaQueryUp.md} {
    grid-template-columns: 1fr 1fr 1fr;
  }

  &.sold-out {
    grid-template-columns: 1fr 2fr;
  }
`

const TitleSection = styled.div`
  ${mediaQueryUp.sm} {
    grid-column: 1 / span 2;
  }

  ${mediaQueryUp.md} {
    grid-column: 1;
  }

  > * + * {
    margin-top: ${rem(12)};
  }
`

const DescriptionSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  grid-column: 1;
  grid-row: 3;

  > * + * {
    margin-top: ${rem(12)};
  }

  ${mediaQueryUp.sm} {
    align-items: flex-start;
    grid-column: 1;
    grid-row: 2;
  }

  ${mediaQueryUp.md} {
    grid-column: 2;
    grid-row: 1;
  }
`

const Description = styled(BodyText)`
  > * + * {
    margin-top: ${rem(8)};
  }

  ${mediaQueryUp.md} {
    > * + * {
      margin-top: ${rem(16)};
    }
  }
`

const BookingSection = styled.div`
  position: relative;
  grid-column: 1;
  grid-row: 2;

  ${mediaQueryUp.sm} {
    grid-column: 2;
    grid-row: 2;
  }

  ${mediaQueryUp.md} {
    grid-column: 3;
    grid-row: 1;
  }

  > * + * {
    margin-top: ${rem(16)};
  }
`

const PackagePrices = styled(BodyText)`
  display: flex;
  position: relative;

  > * {
    flex: 1 1 50%;
    padding-right: ${rem(8)};
  }

  > * + * {
    padding-left: ${rem(8)};
    border-left: 1px solid ${props => props.theme.palette.neutral.five};
  }
`

const Stack = styled.div`
  & > * + * {
    margin-top: ${rem(8)}
  }
`

const PriceButton = styled(OfferBookButton)`
  margin-top: ${rem(12)};
  width: 100%;
`

const ViewPhotosButton = styled(TextButton)`
  display: flex;
  margin-left: auto;
  margin-top: ${rem(24)};
`

const PackagePrice = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  position: relative;
`

const HotelAndFlightText = styled(Caption)`
  /* Space for the plane icon, which isn't rem, thus this isn't */
  padding-left: 28px;
`

const HotelAndFlightIcon = styled(LinePlaneFlyIcon)`
  position: absolute;
  left: ${rem(8)};
  top: ${rem(-4)};
`

const DetailsPane = styled.div`
  padding: ${rem(24)};

  > * + * {
    margin-top: ${rem(16)};
  }

  ${mediaQueryUp.sm} {
    padding: ${rem(36)} ${rem(48)};
    display: flex;

    > * {
      margin-top: 0;
      flex: 1 1 50%;

      &:nth-child(2) {
        /* First child is the close cross */
        padding-right: ${rem(30)};
      }

      &:last-child {
        padding-left: ${rem(30)};
      }
    }
  }
`

const DetailsDescription = styled(MarkdownRender)`
  margin-top: ${rem(12)};
`

const AmenityList = styled(List)`
  margin-top: ${rem(12)};
  margin-bottom: 0;
  font-size: ${props => props.theme.fontSize.sm};

  ${mediaQueryUp.sm} {
    columns: 2;
  }
`

const PackageImage = styled(OfferPageRoomGallery)`
  width: calc(100% + ${rem(48)});
  margin-left: ${rem(-24)};

  ${mediaQueryUp.md} {
    margin: 0;
    margin-top: ${rem(12)};
    width: 100%;
  }
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  margin-top: ${rem(20)};
`

const AuthWrapper = styled.div`
  flex: 1;

  & > * + * {
    margin-top: ${rem(8)};
  }
`

const AuthButtonsWrapper = styled.div`
  & > * + * {
    margin-left: ${rem(8)};
  }
`

const imageProps: ImageParams = {
  xs: '32:13',
  sm: '16:6',
  md: '16:11',
}

interface Props {
  offer: App.Offer;
  packageView: OfferPackageOptionView;
  flightOrigin?: App.Airport;
  flightDestination?: App.Airport;
  rooms: Array<App.Occupants>;
  checkIn: moment.Moment;
  duration: number;
  fetching?: boolean;
}

function LastMinutePackageOption(props: Props) {
  const {
    offer,
    packageView,
    flightOrigin,
    flightDestination,
    rooms,
    checkIn,
    duration,
    fetching,
  } = props

  const {
    package: pkg,
    flightPrice,
  } = packageView

  const [showDetails, onToggleDetails] = useToggle()
  const dispatch = useAppDispatch()
  const offerPageDispatch = useOfferPageDispatch()
  const showLightbox = useContext(LightBoxContext)
  const openGallery = () => showLightbox(<ImageGalleryLightbox images={pkg.roomType.images} />)

  const flightsEnabled = flightOrigin && offer.offerFlightsEnabled && (packageView.flightsAvailable || offer.bundledWithFlightsOnly)
  const hasFlights = !!flightPrice && !offer.flightCacheDisabled

  const onToggleFilter = useCallback((e: React.MouseEvent<HTMLButtonElement> | React.MouseEvent<HTMLAnchorElement>) => {
    e.stopPropagation()
    offerPageDispatch({ type: OfferPageActions.setRoom, room: pkg.roomType })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: e.currentTarget.dataset.filter as OfferPageFilterTypes })
  }, [offerPageDispatch, pkg.roomType])

  const availableForPurchase = useIsAvailableForPurchase()
  const showPrices = packageView.capacityValid && packageView.hotelAvailable
  const sortedAmenities = useMemo(() => sortBy(pkg.roomType.amenities, a => a.name, 'asc'), [pkg.roomType])
  const canBuyNow = !!checkIn && !!rooms && packageView.fromCalendarInfo
  const priceTextColour = useHotelPriceColour(packageView.taxesAndFees)
  const showIncludedGuestsExceeded = useMemo(() => !pkg.roomRate?.extraGuestSurcharges.length && includedGuestsExceededItems(rooms || [], pkg, offer), [pkg, rooms, offer])

  const departureTooEarly = useMemo(() => {
    const minDay = 1
    return !!checkIn && checkIn.diff(moment(), 'days') < minDay
  }, [checkIn])

  const inclusions = useInclusionTravelPeriodFilter(pkg.inclusions, checkIn, checkIn?.clone().add(duration, 'days'))
  const bonusInclusions = useInclusionTravelPeriodFilter(pkg.bonusInclusions, checkIn, checkIn?.clone().add(duration, 'days'))

  return (
    <Pane shadow data-testid="package-option">
      <ScrollAnchor id={pkg.roomType.id} vOffset={rem(80)} />
      <ContentGrid className={cn({ 'sold-out': !availableForPurchase })}>
        <TitleSection>
          <TitleGrid
            title={pkg.roomType.name ? <Heading variant="heading4">{pkg.name}</Heading> : undefined}
            bookmarkButton={availableForPurchase && packageView.capacityValid && (
              <TripGuard>
                <BookmarkButton offer={offer} roomTypeId={pkg.id} />
              </TripGuard>
            )}
          />
          {pkg.roomType.images.length > 0 &&
            <PackageImage
              room={pkg.roomType}
              imageProps={imageProps}
              offer={offer}
            />}
        </TitleSection>
        <DescriptionSection>
          <Description variant="medium">
            {(pkg.roomType.name || pkg.roomType.description) && <div>
              {pkg.roomType.name && <BodyText variant="large" weight="bold">{pkg.roomType.name}</BodyText>}
              {pkg.roomType.description && <BodyText variant="large">{pkg.roomType.description}</BodyText>}
            </div>}
            {pkg.roomType.sizeSqm &&
              <div>
                <strong>Room Size:</strong> {pkg.roomType.sizeSqm} m&sup2;
              </div>
            }
            {bonusInclusions && <Stack>
              <StructureLabel variant="premium">BONUS inclusions:</StructureLabel>
              <MarkdownRender fontSize="medium" content={bonusInclusions} type="compact" />
            </Stack>}
            {inclusions && <PackageDescription>
              {!bonusInclusions && <BodyText variant="medium" weight="bold">Package highlights:</BodyText>}
              {bonusInclusions && <BodyText variant="medium" weight="bold">You also get:</BodyText>}
              <MarkdownRender fontSize="medium" type="compact" content={inclusions} />
            </PackageDescription>}
          </Description>
          <BodyText variant="medium">
            <SmallToggleButton
              on={showDetails}
              onClick={onToggleDetails}
            >
              {showDetails ? 'Hide' : 'View'} all inclusions &amp; details
            </SmallToggleButton>
          </BodyText>
        </DescriptionSection>
        {availableForPurchase && <BookingSection>
          <WalledContent
            enforced={offer.walledGarden}
            wall={(
              <AuthWrapper>
                <BodyTextBlock weight="semi-bold" variant="medium" startIcon={<PriceTagIcon size="XS" />}>
                  Members get access to exclusive rates
                </BodyTextBlock>
                <AuthButtonsWrapper>
                  <TextButton kind="secondary" onClick={() => dispatch(accountAccessShowLogin())}>
                    Log In
                  </TextButton>
                  <TextButton kind="primary" onClick={() => dispatch(accountAccessShowLogin())}>
                    Sign up for free
                  </TextButton>
                </AuthButtonsWrapper>
              </AuthWrapper>)}
          >
            {showPrices &&
              <PackagePrices variant="small">
                <PackagePrice data-testid="pricing">
                  <div>
                    <Caption variant="large" weight="bold">Hotel</Caption>
                    <Caption variant="medium">Total {!checkIn && 'from'}</Caption>
                    <Heading variant="heading6" colour={priceTextColour}>
                      <FormatCurrency value={packageView.hotelPrice} />
                    </Heading>
                    <TaxesAndFees
                      property={offer.property}
                      taxAmount={packageView.taxesAndFees}
                    />
                    {pkg.discountPercent > 0 && <LastMinuteDiscountText
                      discountPercent={pkg.discountPercent}
                      data-testid="last-minute-percentage-box"
                    />}
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                  </div>
                  {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                    type="exact"
                    offer={offer}
                    totalCost={packageView.hotelPrice}
                    numberOfNights={pkg.duration}
                  />}
                  <PriceButton
                    pkg={packageView.package}
                    data-testid={cn('HotelBookingBtn', 'buy-now-button')}
                    disabled={fetching}
                  >
                    {canBuyNow ? 'Book now' : 'Check availability'}
                  </PriceButton>
                </PackagePrice>
                {flightsEnabled && <PackagePrice data-testid="flights-pricing">
                  <HotelAndFlightText variant="large" weight="bold">
                    <HotelAndFlightIcon /> Hotel + flights
                  </HotelAndFlightText>
                  {!hasFlights && flightOrigin.code === flightDestination?.code && (
                    <ChangeDepartureWrapper>
                      <BodyText variant="large" colour="neutral-two">
                        No flights available from {flightOrigin.name}. Please select another airport.
                      </BodyText>
                      <TextLink
                        onClick={onToggleFilter}
                        data-filter="airport"
                      >
                        Change departure airport
                      </TextLink>
                    </ChangeDepartureWrapper>
                  )}
                  {flightOrigin.code !== flightDestination?.code && departureTooEarly && (
                    <ChangeDepartureWrapper>
                      <BodyText variant="small" colour="neutral-two">
                        Flights cannot be booked online when check-in is within 24 hours.
                      </BodyText>
                      <TextLink
                      onClick={onToggleFilter}
                      data-filter="checkin"
                    >
                        Change departure date
                      </TextLink>
                    </ChangeDepartureWrapper>
                  )}
                  {!hasFlights && flightOrigin.code !== flightDestination?.code && !departureTooEarly && (
                    <BodyText variant="large" colour="neutral-two">
                      Add flights to the hotel package. Select from all
                      major airlines.
                    </BodyText>
                  )}
                  {hasFlights && !departureTooEarly && <>
                    <Caption variant="medium">Total {!checkIn && 'from'}</Caption>
                    <Heading variant="heading6" colour={priceTextColour}>
                      <FormatCurrency value={packageView.hotelPrice + packageView.flightPrice} />
                      <Caption variant="medium" as="sup">*</Caption>
                    </Heading>
                    {!rooms && <Caption variant="medium" colour="neutral-three">
                      Two Adults
                    </Caption>}
                    <TaxesAndFees
                      property={offer.property}
                      taxAmount={packageView.taxesAndFees}
                    />
                    {showIncludedGuestsExceeded && (
                      <AdditionalGuestsPopup
                        complex={false}
                        description="+ Extra guest surcharge"
                        modalContent={pkg.roomPolicyDescription}
                      />
                    )}
                    {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
                      type="exact"
                      offer={offer}
                      totalCost={packageView.hotelPrice}
                      numberOfNights={pkg.duration}
                    />}
                  </>}
                  <AtolProtectedTooltip />
                  {flightOrigin.code !== flightDestination?.code && !departureTooEarly && (
                    <PriceButton
                      pkg={packageView.package}
                      data-buytype="flights"
                      data-testid="HotelAndFlightsBtn"
                      disabled={fetching}
                    >
                      {canBuyNow ? 'Book now' : 'Check availability'}
                    </PriceButton>
                  )}
                </PackagePrice>}
                <LoadingIndicator visible={fetching} floating opaque />
              </PackagePrices>
            }
            {!showPrices &&
              <div>
                {packageView.hotelAvailable && !packageView.capacityValid &&
                  <BodyText variant="medium">
                    This {config.brandConfig.travelSynonym} cannot accommodate your number of guests.<br /><br />
                    Please select a different {config.brandConfig.travelSynonym} or{' '}
                    <TextLink onClick={onToggleFilter} data-filter="capacity">
                      add another {offer.saleUnit}
                    </TextLink>
                  </BodyText>
                }
                {!packageView.hotelAvailable && <BodyText variant="large" weight="bold">SOLD OUT</BodyText>}
                {!packageView.hotelAvailable && checkIn &&
                  <BodyText variant="medium">
                    This {config.brandConfig.travelSynonym} is not available for your selected dates.
                    Please select a different {config.brandConfig.travelSynonym} or{' '}
                    <TextLink onClick={onToggleFilter} data-filter="checkin">
                      change your dates
                    </TextLink>
                  </BodyText>
                }
              </div>
            }
          </WalledContent>
        </BookingSection>}
      </ContentGrid>
      <PaneDetailsSection isOpen={showDetails} onToggle={onToggleDetails}>
        <DetailsPane>
          {(pkg.description || bonusInclusions) && <Stack>
            <BodyText variant="large" weight="bold">All {config.brandConfig.travelSynonym} inclusions:</BodyText>
            <BodyText variant="medium">
              {bonusInclusions && (<Stack>
                <StructureLabel variant="premium">BONUS inclusions:</StructureLabel>
                <DetailsDescription fontSize="medium" content={bonusInclusions} type="compact" />
                {pkg.description && <BodyText variant="large" weight="bold">You also get:</BodyText>}
              </Stack>)}
              <MarkdownRender fontSize="medium" content={pkg.description} type="compact" />
            </BodyText>
          </Stack>}
          <div>
            <BodyText variant="large" weight="bold">About The Room</BodyText>
            <BodyText variant="medium">
              <AmenityList>
                {sortedAmenities.map(amenity =>
                  <li key={amenity.id}>{amenity.name}</li>,
                )}
              </AmenityList>
              {pkg.roomPolicyDescription &&
                <>
                  <BodyText variant="large" weight="bold" as="p">Room policies:</BodyText>
                  <DetailsDescription fontSize="medium" content={pkg.roomPolicyDescription} type="compact"/>
                </>}
            </BodyText>
            <ViewPhotosButton kind="tertiary" outdent="left" startIcon={<LineCameraIcon />} onClick={openGallery}>
              View room photos
            </ViewPhotosButton>
          </div>
        </DetailsPane>
      </PaneDetailsSection>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState, ownProps: Props) => ({
  rooms: state.rooms,
  flightOrigin: state.airport,
  checkIn: state.checkInDate,
  duration: state.duration,
  fetching: !!appState.calendar.calendarsLoading[ownProps.packageView.package.uniqueKey],
}))(LastMinutePackageOption)
